import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Crown, 
  CheckCircle, 
  Star, 
  MapPin, 
  ChartBar, 
  Sparkle,
  Lightning,
  Users,
  CreditCard,
  ArrowRight,
  Gift,
  Phone
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [launchOfferInfo, setLaunchOfferInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [mbwayInfo, setMbwayInfo] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, offerRes] = await Promise.all([
        fetch(`${API_URL}/subscription/status`, { credentials: 'include' }),
        fetch(`${API_URL}/subscription/launch-offer-available?city=${user?.city || 'lisboa'}`)
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setSubscriptionStatus(statusData);
      }

      if (offerRes.ok) {
        const offerData = await offerRes.json();
        setLaunchOfferInfo(offerData);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados da subscrição');
    } finally {
      setLoading(false);
    }
  }, [user?.city]);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    const maxAttempts = 5;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/payments/status/${sessionId}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();

          if (data.status === 'paid') {
            toast.success('Pagamento confirmado! A sua subscrição foi ativada.');
            fetchData();
            window.history.replaceState(null, '', window.location.pathname);
            setCheckingPayment(false);
            return;
          } else if (data.status === 'expired') {
            toast.error('Sessão de pagamento expirada. Por favor tente novamente.');
            setCheckingPayment(false);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('A verificar pagamento... Por favor aguarde ou verifique o seu email.');
          setCheckingPayment(false);
        }
      } catch (error) {
        setCheckingPayment(false);
      }
    };

    poll();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, fetchData, checkPaymentStatus]);

  const handleSubscribe = async (planId, method = 'card') => {
    setProcessingPlan(planId);
    try {
      const response = await fetch(`${API_URL}/payments/checkout?plan_id=${planId}&payment_method=${method}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_method === 'mbway') {
          setMbwayInfo(data);
          toast.success('Pedido de pagamento MB Way criado!');
        } else {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao processar pagamento');
      }
    } catch (error) {
      toast.error('Erro ao iniciar checkout');
    } finally {
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const isPro = subscriptionStatus?.is_pro_active;
  const hasLaunchOffer = subscriptionStatus?.has_launch_offer;
  const leadsRemaining = subscriptionStatus?.leads_remaining;
  const leadsThisMonth = subscriptionStatus?.leads_this_month || 0;
  const maxLeadsFree = subscriptionStatus?.max_leads_free || 2;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold font-outfit mb-2">Subscrição</h1>
          <p className="text-muted-foreground mb-8">
            Escolha o plano ideal para o seu negócio
          </p>

          {checkingPayment && (
            <Card className="mb-6 border-primary">
              <CardContent className="py-6 flex items-center gap-4">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p>A verificar o seu pagamento...</p>
              </CardContent>
            </Card>
          )}

          {/* Current Status */}
          {subscriptionStatus && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isPro ? (
                    <>
                      <Crown weight="fill" className="w-5 h-5 text-accent" />
                      Plano Pro Ativo
                    </>
                  ) : (
                    <>
                      <Users className="w-5 h-5" />
                      Plano Gratuito
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {hasLaunchOffer && (
                  <div className="flex items-center gap-2 text-sm bg-accent/10 text-accent-foreground p-3 rounded-lg">
                    <Gift weight="fill" className="w-5 h-5 text-accent" />
                    <span>
                      <strong>Oferta de Lançamento!</strong> {subscriptionStatus.launch_offer_days_remaining} dias restantes de Pro grátis
                    </span>
                  </div>
                )}

                {isPro && !hasLaunchOffer && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle weight="fill" className="w-5 h-5 text-primary" />
                    <span>
                      Subscrição ativa por mais {subscriptionStatus.pro_days_remaining} dias
                    </span>
                  </div>
                )}

                {!isPro && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span>Contactos este mês</span>
                      <span className="font-medium">{leadsThisMonth} / {maxLeadsFree}</span>
                    </div>
                    <Progress value={(leadsThisMonth / maxLeadsFree) * 100} className="h-2" />
                    {leadsRemaining === 0 && (
                      <p className="text-sm text-destructive mt-2">
                        Atingiu o limite mensal. Faça upgrade para receber mais contactos!
                      </p>
                    )}
                  </div>
                )}

                {subscriptionStatus.identity_verified && (
                  <Badge className="bg-primary/10 text-primary">
                    <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
                    Identidade Verificada
                  </Badge>
                )}
              </CardContent>
            </Card>
          )}

          {/* Launch Offer Banner */}
          {launchOfferInfo?.available && !hasLaunchOffer && (
            <Card className="mb-8 bg-gradient-to-r from-accent/10 to-secondary/10 border-accent/20">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Gift weight="fill" className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Oferta de Lançamento!</h3>
                    <p className="text-muted-foreground mt-1">
                      Os primeiros {launchOfferInfo.max_spots} prestadores em cada cidade ganham{' '}
                      <strong>{launchOfferInfo.free_months} meses de Plano Pro GRÁTIS</strong>!
                    </p>
                    <p className="text-sm font-medium text-accent mt-2">
                      Apenas {launchOfferInfo.spots_remaining} lugares restantes na sua cidade!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <Card className={!isPro ? 'border-primary' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Plano Gratuito</CardTitle>
                  {!isPro && <Badge>Atual</Badge>}
                </div>
                <CardDescription>Para começar</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">0€</span>
                  <span className="text-muted-foreground">/mês</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                    Perfil básico
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                    Máximo {maxLeadsFree} contactos por mês
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                    Chat com clientes
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-4 h-4 flex-shrink-0 text-center">—</span>
                    Sem destaque no mapa
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-4 h-4 flex-shrink-0 text-center">—</span>
                    Sem estatísticas avançadas
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className={`relative overflow-hidden ${isPro ? 'border-primary' : 'border-accent'}`}>
              <div className="absolute top-0 right-0 bg-accent text-accent-foreground px-3 py-1 text-xs font-medium rounded-bl-lg">
                Recomendado
              </div>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Crown weight="fill" className="w-5 h-5 text-accent" />
                    Plano Pro
                  </CardTitle>
                  {isPro && <Badge className="bg-primary">Ativo</Badge>}
                </div>
                <CardDescription>Para profissionais</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">4,99€</span>
                  <span className="text-muted-foreground">/mês</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <Lightning weight="fill" className="w-4 h-4 text-accent flex-shrink-0" />
                    <strong>Contactos ilimitados</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <MapPin weight="fill" className="w-4 h-4 text-accent flex-shrink-0" />
                    Destaque no mapa
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Star weight="fill" className="w-4 h-4 text-accent flex-shrink-0" />
                    Perfil em primeiro lugar
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <ChartBar weight="fill" className="w-4 h-4 text-accent flex-shrink-0" />
                    Estatísticas avançadas
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkle weight="fill" className="w-4 h-4 text-accent flex-shrink-0" />
                    Badge de prestador verificado
                  </li>
                </ul>

                {!isPro && (
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleSubscribe('pro_monthly', 'card')}
                      disabled={processingPlan === 'pro_monthly'}
                      className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
                      data-testid="subscribe-pro-btn"
                    >
                      {processingPlan === 'pro_monthly' ? (
                        'A processar...'
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pagar com Cartao
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSubscribe('pro_monthly', 'mbway')}
                      disabled={processingPlan === 'pro_monthly'}
                      className="w-full rounded-full"
                      data-testid="subscribe-pro-mbway-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Pagar com MB Way
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pay per Contact Option */}
          <Card className="mt-6">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Pay-per-Contacto
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Não quer mensalidade? Pague apenas 4€ por cada contacto qualificado.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold">4€</p>
                    <p className="text-xs text-muted-foreground">por contacto</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleSubscribe('pay_per_lead', 'card')}
                      disabled={processingPlan === 'pay_per_lead'}
                      className="rounded-full"
                      data-testid="pay-per-lead-btn"
                    >
                      Cartao
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSubscribe('pay_per_lead', 'mbway')}
                      disabled={processingPlan === 'pay_per_lead'}
                      className="rounded-full"
                      data-testid="pay-per-lead-mbway-btn"
                    >
                      MB Way
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MB Way Pending Info */}
          {mbwayInfo && (
            <Card className="mt-6 border-amber-500/50 bg-amber-50 dark:bg-amber-950/20" data-testid="mbway-info-card">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                    <Phone weight="fill" className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">Pagamento MB Way Pendente</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Pague <strong>{mbwayInfo.amount}EUR</strong> por MB Way para o numero:
                    </p>
                    <div className="bg-white dark:bg-card rounded-lg p-4 border mb-3">
                      <p className="text-2xl font-bold text-center tracking-widest" data-testid="mbway-phone-number">
                        {mbwayInfo.mbway_phone}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {mbwayInfo.instructions}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending MB Way from server */}
          {subscriptionStatus?.pending_mbway_payment && !mbwayInfo && (
            <Card className="mt-6 border-amber-500/50 bg-amber-50 dark:bg-amber-950/20" data-testid="mbway-pending-card">
              <CardContent className="py-6">
                <div className="flex items-center gap-3">
                  <Phone weight="fill" className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium">Pagamento MB Way pendente</p>
                    <p className="text-sm text-muted-foreground">
                      Tem um pagamento MB Way a aguardar confirmação. Envie o comprovativo para suporte@passeioperfeito.pt ou pelo chat.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Methods Info */}
          <div className="mt-8 text-center text-sm text-muted-foreground space-y-1">
            <p className="flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              Pagamento seguro via Stripe (Cartao) ou MB Way
            </p>
          </div>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
